import { useEffect, useState } from "react";

// styles
import "../../shared/assets/styles";
import { useStyledButtons } from "../../shared/assets/styles";

// material-ui
import { Button } from "@material-ui/core";

// services
import {
  useControleContext,
  useGlobalContext,
  useNotificacaoDoAppContext,
  usePrevisaoCompra,
} from "../../shared/hooks";

// components
import {
  ContainerDaPagina,
  InformacoesPedido,
  ListagemDeNotificacoes,
  ListagemDeSolicitacoes,
  ModalConfirmarRespostaSolicitacao,
} from "../../shared/components";

import {
  IRespostaSolicitacao,
  ISolicitacaoSenha,
  ISolicitacaoSenhaExcedentes,
  TDirecaoPagina,
  TNotificacaoDoAplicativo,
  TSeverityNotificacao,
  TStatusPermissao,
  TTipoResponderSolicitacao,
} from "../../shared/interfaces";
import {
  AssinaturaDigitalService,
  NotificacaoService,
  PrevisaoDeComprasService,
  SolicitacaoDeSenhaService,
} from "../../shared/services";
import {
  ehAssinaturaDigitalNLar,
  ehContasAPagar,
  fazParteGrupoOrdemCompras,
  handleValidarToken,
  limparIntervalosExistentes,
  obterMensagemApiException,
} from "../../shared/functions";
import { criarAlerta, obterParametrosDaURL } from "../../shared/functions";
import { ListagemDeOpcoes } from "../../shared/components/listagem-de-opcoes";
import { useContasAPagar } from "../../shared/services/context/contas-a-pagar-context";
import { ResumoDeContasAPagarPendenteDeEnvio } from "../../shared/components/resumo-de-contas-a-pagar-pendente-de-envio";
import { ConfiguracaoDeFiltro } from "../../shared/components/configuracao-de-filtro";
import { toCentavos, toReais } from "../../shared/functions/conversao/conversao";
import { ScrollDaLista } from "../../shared/functions/scroll-da-lista";
import { AvisoSolicitacaoJaRespondida } from "../../shared/components/aviso-solicitacao-ja-respondida";
import { configs } from "../../shared/configs";
import { ModalAvisoContasExcedentePreConfirmacao } from "../../shared/components/modal-aviso-contas-excedente-pre-confirmacao";

interface IInformacoesConfirmarRespostaSolicitacao {
  modalAtivo: boolean;
  pedidoSolicitacao: ISolicitacaoSenha;
  statusPermissao: TStatusPermissao;
}

interface IBtnListagemPedidosProps {
  disabled?: boolean;
  direcao: TDirecaoPagina;
  handleChangePagina: (direcao: TDirecaoPagina) => void;
}

export const BtnListagemPedidos: React.FC<IBtnListagemPedidosProps> = ({
  children,
  direcao,
  disabled,
  handleChangePagina,
}) => {
  const classes = useStyledButtons();

  return (
    <Button
      variant="contained"
      className={classes.bttPagination}
      onClick={() => handleChangePagina(direcao)}
      disabled={disabled}
    >
      {children}
    </Button>
  );
};

export const Main: React.FC = () => {
  const {
    inicio_pedidoSelecionado,
    pedidoSelecionado,
    motivoRespostaSolicitacao,
    paginaAtual,
    valorInicialCabecalhoPrevisaoDeCompraSelecionada,
    setPedidoSelecionado,
    setNotificacao,
    setLoading,
    setSolicitacaoSelecionadaRespondida,
    setPaginaAtual,
    setCabecalhoPrevisaoDeCompraSelecionada,
    setPaginacaoItensPedidoSelecionado,
    setItensPedidoSelecionado,
    setParcelasContasAPagarExcedido,
    setFormaPagamentoPrevisaoCompraDetalhada,
  } = useGlobalContext();

  const {
    setNotificacaoDoAppSelecionada,
    setSolicitacaoPendenteSelecionada,
    setAbaSelecionada,
  } = useNotificacaoDoAppContext();

  const {
    listagemDeOpcoesDeRelatorioAtiva,
    setListagemDeOpcoesDeRelatorioAtiva,
  } = useControleContext();

  const [
    modalPreConfirmacaoDeLiberacaoContasExcedenteEstaAberto,
    setModalPreConfirmacaoDeLiberacaoContasExcedenteEstaAberto
  ] = useState(false);

  const { valorInicialDoLoteDeContasAPagar, setValoresDoLoteDeContasAPagar } =
    useContasAPagar();

  const { setAnexosPrevisao } = usePrevisaoCompra();

  const inicioInformacoesConfirmarRespostaSolicitacao: IInformacoesConfirmarRespostaSolicitacao =
    {
      modalAtivo: false,
      pedidoSolicitacao: { ...inicio_pedidoSelecionado },
      statusPermissao: "negar",
    };

  const [
    informacoesConfirmarRespostaSolicitacao,
    setInformacoesConfirmarRespostaSolicitacao,
  ] = useState<IInformacoesConfirmarRespostaSolicitacao>(
    inicioInformacoesConfirmarRespostaSolicitacao
  );

  const [
    estaExibindoAvisoSolicitacaoJaRespondida,
    setEstaExibindoAvisoSolicitacaoJaRespondida,
  ] = useState(false);

  const [
    tipoMensagemSolicitacaoRespondida,
    setTipoMensagemSolicitacaoRespondida,
  ] = useState("");

  const handleFecharModalAvisoSolicitacaoJaRespondida = () => {
    setEstaExibindoAvisoSolicitacaoJaRespondida(false);
    setTipoMensagemSolicitacaoRespondida("");
  };

  const notificar = async (severity: TSeverityNotificacao, message: string) =>
    await criarAlerta(severity, message, setNotificacao);

  // buscar informações sobre o pedido clicado
  const selecionarSolicitacao = async (
    solicitacao: ISolicitacaoSenha,
    indiceSolicitacao: number
  ) => {
    setLoading(true);

    try {
      setPedidoSelecionado({ ...solicitacao });

      ScrollDaLista.guardarElementoParaFoco(indiceSolicitacao);

      setPaginaAtual("pedido-selecionado");

      limparIntervalosExistentes();

      setListagemDeOpcoesDeRelatorioAtiva(false);

      setSolicitacaoSelecionadaRespondida(solicitacao.statusMensagem > 3);

      await SolicitacaoDeSenhaService.AlterarStatusMensagemEUsuarioOrigemEDestinoPorId(
        solicitacao.sequencial
      );

      if (ehContasAPagar(solicitacao)) {
        const valoresDoLoteDeContasAPagarData =
          await SolicitacaoDeSenhaService.ObterValoresDoLoteDeContasAPagar(
            solicitacao.idLote
          );

        const vencimentosDoLote =
          await SolicitacaoDeSenhaService.ObterVencimentosDoLoteDeContasAPagar(
            solicitacao.idLote
          );

        setValoresDoLoteDeContasAPagar({
          ...valoresDoLoteDeContasAPagarData,
          totalGeral: toReais(
            toCentavos(valoresDoLoteDeContasAPagarData.valorDoc) +
              toCentavos(valoresDoLoteDeContasAPagarData.jurosPago || 0) +
              toCentavos(valoresDoLoteDeContasAPagarData.multa || 0) +
              toCentavos(valoresDoLoteDeContasAPagarData.taxaCartorio || 0) -
              toCentavos(valoresDoLoteDeContasAPagarData.desconto || 0)
          ),
          vencimentos: vencimentosDoLote,
        });
      } else
        setValoresDoLoteDeContasAPagar({ ...valorInicialDoLoteDeContasAPagar });
    } catch (error) {
      setValoresDoLoteDeContasAPagar({ ...valorInicialDoLoteDeContasAPagar });

      notificar(
        'error',
        obterMensagemApiException(error),
      );
    }

    if (solicitacao.numOrcPedido && fazParteGrupoOrdemCompras(solicitacao)) {
      try {
        
        const { nLarPrevisaoCompraDTO, nLarPrevisaoCompraItem, solicitacaoSenhaExcedentes } =
          await PrevisaoDeComprasService.ObterInformacoesPeloCodigo({
            codPrevisao: solicitacao.numOrcPedido,
            codLoja: solicitacao.codloja,
            currentPage: 1,
            limit: configs.LIMITE,
            sequencial: solicitacao.sequencial,
          });

        setCabecalhoPrevisaoDeCompraSelecionada({ ...nLarPrevisaoCompraDTO });

        setItensPedidoSelecionado([...nLarPrevisaoCompraItem]);

        setParcelasContasAPagarExcedido([...solicitacaoSenhaExcedentes])

        setPaginacaoItensPedidoSelecionado({
          paginaAtual: 1,
          totalDePaginas: nLarPrevisaoCompraDTO.totalPaginas,
        });
      } catch {
        setCabecalhoPrevisaoDeCompraSelecionada({
          ...valorInicialCabecalhoPrevisaoDeCompraSelecionada,
        });

        setItensPedidoSelecionado([]);

        setPaginacaoItensPedidoSelecionado({
          paginaAtual: 0,
          totalDePaginas: 0,
        });
      }

      if (solicitacao.numOrcPedido && solicitacao.codloja) {
        try {
          const formaPagamentoDetalhada =
            await PrevisaoDeComprasService.ObterFormaDePagamentoDetalhada(
              solicitacao.numOrcPedido,
              solicitacao.codloja
            );

          setFormaPagamentoPrevisaoCompraDetalhada(formaPagamentoDetalhada);
        } catch {
          setFormaPagamentoPrevisaoCompraDetalhada([]);
        }

        try {
          const anexosEncontrados = await PrevisaoDeComprasService.ObterAnexos(
            solicitacao.codloja,
            solicitacao.numPrevisao || solicitacao.numOrcPedido,
            solicitacao.sequencial
          );

          setAnexosPrevisao([...anexosEncontrados]);
        } catch {
          setAnexosPrevisao([]);
        }
      } else {
        setFormaPagamentoPrevisaoCompraDetalhada([]);
        setAnexosPrevisao([]);
      }
    } else {
      setCabecalhoPrevisaoDeCompraSelecionada({
        ...valorInicialCabecalhoPrevisaoDeCompraSelecionada,
      });

      setItensPedidoSelecionado([]);

      setFormaPagamentoPrevisaoCompraDetalhada([]);

      setAnexosPrevisao([]);

      setPaginacaoItensPedidoSelecionado({ paginaAtual: 0, totalDePaginas: 0 });
    }

    setLoading(false);
  };

  const exibirInformacoesPedidoSolicitadoPeloApp = async (
    sequencial: number
  ) => {
    try {
      const solicitacaoBuscada =
        await SolicitacaoDeSenhaService.ObterSolicitacaoSenhaPorId(sequencial);

      selecionarSolicitacao(solicitacaoBuscada, 0);
    } catch {}
  };

  const handleClickAcaoAutorizar = (
    parcelasContasAPagarExcedido: ISolicitacaoSenhaExcedentes[]
  ) => {
    if(parcelasContasAPagarExcedido.length > 0)
      setModalPreConfirmacaoDeLiberacaoContasExcedenteEstaAberto(true);
    else
      handleClickRespostaSolicitacao({
        pedidoSolicitacao: { ...pedidoSelecionado },
        statusPermissao: "autorizar",
      })
  }

  const handleClickRespostaSolicitacao = (
    respostaSolicitacao: IRespostaSolicitacao
  ) => {
    const { pedidoSolicitacao, statusPermissao } = respostaSolicitacao;
    setInformacoesConfirmarRespostaSolicitacao({
      modalAtivo: true,
      pedidoSolicitacao,
      statusPermissao,
    });
  };

  const voltarModalConfirmacaoParaEstadoInicial = async () =>
    setInformacoesConfirmarRespostaSolicitacao({
      ...inicioInformacoesConfirmarRespostaSolicitacao,
    });

  const extrairTipoMensagem = (mensagem: string): string | null => {
    const regex = /#(\w+)/;
    const match = mensagem.match(regex);
    return match ? match[1] : null;
  };

  const handleClickConfirmarRespostaSolicitacao = async () => {
    setLoading(true);

    try {
      const tipoMensagem: TTipoResponderSolicitacao =
        informacoesConfirmarRespostaSolicitacao.statusPermissao === "autorizar"
          ? "CONFIRMACAO"
          : "RECUSADO";

      const {
        sequencial,
        numOrcPedido: NumOrc,
        codLojaPedido: CodLoja,
      } = pedidoSelecionado;

      if (ehAssinaturaDigitalNLar(pedidoSelecionado)) {
        const aSolicitacaoFoiRespondidaPorOutroGerente =
          await AssinaturaDigitalService.VerificarSolicitacaoRespondida({
            NumOrc,
            CodLoja,
          });

        if (aSolicitacaoFoiRespondidaPorOutroGerente) {
          setSolicitacaoSelecionadaRespondida(true);

          setLoading(false);

          voltarModalConfirmacaoParaEstadoInicial();

          notificar(
            "info",
            `A solicitação de senha para o orçamento ${NumOrc} já foi respondida por outro gerente`
          );

          return;
        }

        if (tipoMensagem === "CONFIRMACAO")
          await AssinaturaDigitalService.ValidarContrato({ NumOrc, CodLoja });
        else await AssinaturaDigitalService.NegarContrato({ NumOrc, CodLoja });
      }

      if (ehContasAPagar(pedidoSelecionado)) {
        if (tipoMensagem === "CONFIRMACAO")
          await SolicitacaoDeSenhaService.AutorizarLiberacoesBaixaContasAPagar(
            pedidoSelecionado.idLote,
            "S"
          );
        else
          await SolicitacaoDeSenhaService.AutorizarLiberacoesBaixaContasAPagar(
            pedidoSelecionado.idLote,
            "N"
          );
      }

      await SolicitacaoDeSenhaService.ResponderSolicitacao({
        sequencial,
        motivo: motivoRespostaSolicitacao || "",
        tipoMensagem,
      });

      sessionStorage.setItem(
        "mensagem-respondeu-solicitacao",
        `Solicitação ${
          tipoMensagem === "CONFIRMACAO" ? "autorizada" : "recusada"
        } com sucesso!`
      );

      window.location.reload();
    } catch (error) {
      const mensagem = obterMensagemApiException(error).toUpperCase() || "";

      if (mensagem.includes("A SOLICITAÇÃO JÁ FOI RESPONDIDA!")) {
        setEstaExibindoAvisoSolicitacaoJaRespondida(true);

        const tipoMensagemRespondida = extrairTipoMensagem(mensagem);

        setTipoMensagemSolicitacaoRespondida(tipoMensagemRespondida || "");
      } else {
        notificar(
          'error',
          obterMensagemApiException(error),
        );
      }
    }

    setLoading(false);
  };

  const selecionarNotificacaoDoApp = (
    notificacao: TNotificacaoDoAplicativo
  ) => {
    setLoading(true);

    try {
      setNotificacaoDoAppSelecionada({ ...notificacao });

      setPaginaAtual("notificacoes");
    } catch (error) {
      notificar(
        'error',
        obterMensagemApiException(error),
      );
    }

    setLoading(false);
  };

  const selecionarNotificacaoSolicitacaoPendente = (
    solicitacaoPendente: ISolicitacaoSenha
  ) => {
    setLoading(true);

    try {
      setSolicitacaoPendenteSelecionada({ ...solicitacaoPendente });

      setAbaSelecionada("pendentes");

      setPaginaAtual("notificacoes");
    } catch (error) {
      notificar(
        'error',
        obterMensagemApiException(error),
      );
    }

    setLoading(false);
  };

  const exibirNotificacaoInformadaPelaURL = async (idMensagem: number) => {
    try {
      const notificacaoBuscada = await NotificacaoService.ObterNotificacoesPorId(
        idMensagem
      );

      selecionarNotificacaoDoApp(notificacaoBuscada);
    } catch {}
  };

  const exibirSolicitacaoPendenteSolicitadaPelaURL = async (
    sequencial: number
  ) => {
    try {
      const solicitacaoPendente =
        await SolicitacaoDeSenhaService.ObterSolicitacaoSenhaPorId(sequencial);

      selecionarNotificacaoSolicitacaoPendente(solicitacaoPendente);
    } catch {}
  };

  useEffect(() => {
    const { token, sequencial, tipoDaMensagem } = obterParametrosDaURL();

    const tokenDecode = handleValidarToken(token);

    if(tokenDecode && tokenDecode.CodCliente.trim() === '431') {
      window.location.href = `https://${window.location.hostname}/nosso-lar${configs.BASENAME}?token=${token}`

      return;
    }


    if(tokenDecode && (tokenDecode.CodCliente.trim() === '4607' || tokenDecode.CodCliente.trim() === '6194')) {
      window.location.href = `https://${window.location.hostname}/lojas-centro${configs.BASENAME}?token=${token}`

      return;
    }

    if (token && sequencial) {
      if (tipoDaMensagem === "solicitacao") {
        exibirInformacoesPedidoSolicitadoPeloApp(sequencial);
      } else if (tipoDaMensagem === "notificacao") {
        exibirNotificacaoInformadaPelaURL(sequencial);
      } else if (tipoDaMensagem === "solicitacaopendente") {
        exibirSolicitacaoPendenteSolicitadaPelaURL(sequencial);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const mensagemRespondeuSolicitacao = sessionStorage.getItem(
      "mensagem-respondeu-solicitacao"
    );

    if (mensagemRespondeuSolicitacao) {
      notificar("success", mensagemRespondeuSolicitacao);

      sessionStorage.removeItem("mensagem-respondeu-solicitacao");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {informacoesConfirmarRespostaSolicitacao.modalAtivo && (
        <ModalConfirmarRespostaSolicitacao
          sequencial={
            informacoesConfirmarRespostaSolicitacao.pedidoSolicitacao.sequencial
          }
          statusPermissao={
            informacoesConfirmarRespostaSolicitacao.statusPermissao
          }
          voltarModalConfirmacaoParaEstadoInicial={
            voltarModalConfirmacaoParaEstadoInicial
          }
          onClickConfirmar={handleClickConfirmarRespostaSolicitacao}
        />
      )}

      {estaExibindoAvisoSolicitacaoJaRespondida && (
        <AvisoSolicitacaoJaRespondida
          sequencial={pedidoSelecionado.sequencial}
          tipoMensagemSolicitacaoRespondida={tipoMensagemSolicitacaoRespondida}
          onClickVoltar={handleFecharModalAvisoSolicitacaoJaRespondida}
        />
      )}

      {modalPreConfirmacaoDeLiberacaoContasExcedenteEstaAberto && (
        <ModalAvisoContasExcedentePreConfirmacao
          onClickConfirmar={() => 
            handleClickRespostaSolicitacao({
              pedidoSolicitacao: { ...pedidoSelecionado },
              statusPermissao: "autorizar",
            })
          }
          sequencial={pedidoSelecionado.sequencial}
          fecharModal={() => 
            setModalPreConfirmacaoDeLiberacaoContasExcedenteEstaAberto(false)
          }
        />
      )}

      <ContainerDaPagina>
        {paginaAtual === "notificacoes" && (
          <ListagemDeNotificacoes
            selecionarNotificacaoDoApp={selecionarNotificacaoDoApp}
            selecionarNotificacaoSolicitacaoPendente={
              selecionarNotificacaoSolicitacaoPendente
            }
          />
        )}

        {paginaAtual === "resumo-contas-a-pagar-pendente-de-envio" && (
          <ResumoDeContasAPagarPendenteDeEnvio />
        )}

        {paginaAtual === "solicitacoes" && (
          <ListagemDeSolicitacoes
            selecionarSolicitacao={selecionarSolicitacao}
          />
        )}

        {paginaAtual === "pedido-selecionado" &&
          !listagemDeOpcoesDeRelatorioAtiva && (
            <InformacoesPedido
              handleClickRespostaSolicitacao={handleClickRespostaSolicitacao}
              handleClickAcaoAutorizar={handleClickAcaoAutorizar}
            />
        )}

        {paginaAtual === "pedido-selecionado" &&
          listagemDeOpcoesDeRelatorioAtiva && <ListagemDeOpcoes />}

        {paginaAtual === "configuracoes" && <ConfiguracaoDeFiltro />}
      </ContainerDaPagina>
    </>
  );
};
